import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import LogRocket from 'logrocket';

import { graphqlSchema } from '../services/graphql.schema';
import { FiscalpopProfile, ProfessionalProfile, ProfileQL } from '../models/Profile';
import { register } from '../serviceWorker';

const LEGACY_AUTH_TOKEN = 'prof_profileid';
const FP_SESSION_COOKIE = 'fp_prof_profileid';
const HOME_REDIRECT_URL = process.env.REACT_APP_HOME_REDIRECT_URL!;

// The provider only mounts on protected routes (App.tsx splits public pages out of this
// subtree), so an auth failure always means "kick this user away" — no path allow-list needed.
function redirectToAuthFail() {
    // Clear stale identity so a stuck cookie can't trap the user in a refetch loop.
    localStorage.removeItem(LEGACY_AUTH_TOKEN);
    const domain = '.fiscalpop.com';
    document.cookie = `${LEGACY_AUTH_TOKEN}=;domain=${domain};path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
    document.cookie = `${FP_SESSION_COOKIE}=;domain=${domain};path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
    window.location.replace(HOME_REDIRECT_URL);
}

interface CurrentUserContextValue {
    currentUser: ProfileQL | null;
    userFetched: boolean;
    modifyProfessionalProfile: (props: Partial<ProfessionalProfile>) => void;
    modifyFiscalpopProfile: (props: Partial<FiscalpopProfile>) => void;
    recallCurrentUser: () => Promise<void>;
}

const CurrentUserContext = createContext<CurrentUserContextValue | undefined>(undefined);

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
    const apolloClient = useApolloClient();
    const [currentUser, setCurrentUser] = useState<ProfileQL | null>(null);
    const [userFetched, setUserFetched] = useState(false);
    const identifiedRef = useRef(false);

    const applyUser = useCallback((user: ProfileQL | null) => {
        setCurrentUser(user);
        if (!identifiedRef.current && user?.professionalProfile) {
            identifiedRef.current = true;
            LogRocket.identify(`${user.professionalProfile.email}`, {
                name: `${user.professionalProfile.companyName}`,
                email: user.professionalProfile.email,
                linker: user.professionalProfile.linkerId,
                hasCfdiUsage: !!user?.billing?.stripeSubscriptionCfdiUsage ? 'true' : 'false',
                canBill: !!user?.professionalProfile?.authToken ? 'true' : 'false',
                hasDonePayments: !!user?.billing?.lastPaymentId ? 'true' : 'false',
            });
            // Empty register starts SW without requesting subscription manager (matches prior App.tsx behavior).
            register();
        }
    }, []);

    const fetchMe = useCallback(async () => {
        try {
            const response = await apolloClient.query<{ me: ProfileQL }>({
                query: graphqlSchema.PROFILE.me,
                fetchPolicy: 'network-only',
            });
            const me = response.data?.me ?? null;
            if (!me) {
                redirectToAuthFail();
                return;
            }
            applyUser(me);
        } catch (e) {
            console.error('[CurrentUserContext] Error fetching user: ', e);
            redirectToAuthFail();
        } finally {
            // Flip the loading flag even if a redirect is in flight — the page replace will
            // discard this render in the same tick, but we don't want to risk a stuck loader.
            setUserFetched(true);
        }
    }, [apolloClient, applyUser]);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const modifyProfessionalProfile = useCallback((props: Partial<ProfessionalProfile>) => {
        // Apollo cache results are frozen; rebuild the wrapping profile object instead of mutating it.
        setCurrentUser(curr => curr ? ({
            ...curr,
            professionalProfile: { ...curr.professionalProfile, ...props },
        }) : curr);
    }, []);

    const modifyFiscalpopProfile = useCallback((props: Partial<FiscalpopProfile>) => {
        // Same frozen-cache rebuild as professionalProfile. Allows applying mutation responses
        // (e.g. registerEmisorOnly) without a follow-up `me` refetch.
        setCurrentUser(curr => curr ? ({
            ...curr,
            fiscalpopProfile: { ...(curr.fiscalpopProfile || {}), ...props } as FiscalpopProfile,
        }) : curr);
    }, []);

    const recallCurrentUser = useCallback(() => fetchMe(), [fetchMe]);

    const value: CurrentUserContextValue = {
        currentUser,
        userFetched,
        modifyProfessionalProfile,
        modifyFiscalpopProfile,
        recallCurrentUser,
    };

    return (
        <CurrentUserContext.Provider value={value}>
            {children}
        </CurrentUserContext.Provider>
    );
}

export function useCurrentUser(): CurrentUserContextValue {
    const ctx = useContext(CurrentUserContext);
    if (!ctx) {
        throw new Error('useCurrentUser must be used within a CurrentUserProvider');
    }
    return ctx;
}
